import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const OuterBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MainBox = styled.div`
  width: 100%;
  height: 520px;
  position: relative;
  overflow: hidden;
  border-radius: 0.8rem;
  background-color: #f7f7f7;
  border: 1px solid #efefef;

  @media (max-width: 450px) {
    height: 320px;
  }
  @media only screen and (min-width: 451px) and (max-width: 1020px) {
    height: 400px;
  }
`;

const ImgBox = styled.div`
  background-image: url(${(props) => props.img});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 0.5s ease-in-out;
`;

const ArrowLeft = styled.button`
  text-align: center;
  border: none;
  background-color: rgba(0, 0, 0, 0.45);
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  position: absolute;
  top: 240px;
  left: 1rem;
  font-size: 1.5rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.75);
  }

  svg {
    transform: scale(1.3);
  }

  @media (max-width: 450px) {
    top: 140px;
    width: 3.2rem;
    height: 3.2rem;
  }
`;

const ArrowRight = styled.button`
  text-align: center;
  border: none;
  background-color: rgba(0, 0, 0, 0.45);
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  color: white;
  position: absolute;
  right: 1rem;
  top: 240px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.75);
  }

  svg {
    transform: scale(1.3);
  }

  @media (max-width: 450px) {
    top: 140px;
    width: 3.2rem;
    height: 3.2rem;
  }
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.2rem;
  overflow-x: auto;
  padding: 0.4rem 0.2rem;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
`;

const ThumbnailBox = styled.div`
  width: 70px;
  height: 70px;
  min-width: 70px;
  border-radius: 0.6rem;
  overflow: hidden;
  cursor: pointer;
  border: 2.5px solid ${(props) => (props.$active ? "#511d00" : "#e0e0e0")};
  box-shadow: ${(props) =>
    props.$active ? "0 2px 8px rgba(81, 29, 0, 0.25)" : "none"};
  opacity: ${(props) => (props.$active ? "1" : "0.7")};
  transition: all 0.2s ease;
  background-color: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
    border-color: #511d00;
    transform: translateY(-2px);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 450px) {
    width: 55px;
    height: 55px;
    min-width: 55px;
  }
`;

const ImagesSlider = ({ product }) => {
  const [imgSlides, setImgSlides] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!product) return;
    const list = [];
    if (product.featuredImage) {
      list.push(product.featuredImage);
    }
    if (Array.isArray(product.otherImages) && product.otherImages.length > 0) {
      product.otherImages.forEach((img) => {
        if (img && !list.includes(img)) list.push(img);
      });
    }
    if (list.length === 0 && product.images) {
      const splitArr = product.images.trim().split(" ").filter(Boolean);
      splitArr.forEach((img) => {
        if (img && !list.includes(img)) list.push(img);
      });
    }
    setImgSlides(list);
    setCurrent(0);
  }, [product]);

  const prev = () => {
    if (imgSlides.length <= 1) return;
    setCurrent((prevIdx) => (prevIdx > 0 ? prevIdx - 1 : imgSlides.length - 1));
  };

  const next = () => {
    if (imgSlides.length <= 1) return;
    setCurrent((prevIdx) => (prevIdx < imgSlides.length - 1 ? prevIdx + 1 : 0));
  };

  return (
    <OuterBox>
      <MainBox>
        {imgSlides.map((item, idx) => (
          <ImgBox
            key={idx}
            img={item}
            style={{
              transform: `translateX(${(idx - current) * 100}%)`,
            }}
          />
        ))}
      </MainBox>

      {imgSlides.length > 1 && (
        <>
          <ArrowLeft onClick={prev} aria-label="Previous Image">
            <ChevronLeft />
          </ArrowLeft>
          <ArrowRight onClick={next} aria-label="Next Image">
            <ChevronRight />
          </ArrowRight>
        </>
      )}

      {imgSlides.length > 1 && (
        <ThumbnailsContainer>
          {imgSlides.map((item, idx) => (
            <ThumbnailBox
              key={idx}
              $active={current === idx}
              onClick={() => setCurrent(idx)}
              title={`View slide ${idx + 1}`}
            >
              <img src={item} alt={`Thumbnail ${idx + 1}`} />
            </ThumbnailBox>
          ))}
        </ThumbnailsContainer>
      )}
    </OuterBox>
  );
};

export default ImagesSlider;
